import Tracker from "../../../../prop-trackers/tracker.js";
import PropTracker from "../../../../prop-trackers/prop-tracker.js";
import ValueTracker from "../../../../prop-trackers/value-tracker.js";
import DictTracker from "../../../../prop-trackers/dict-tracker.js";
import TrackerGroup from "../../../../prop-trackers/tracker-group.js";
import { Combiner, ValueCombiner } from "../../../../prop-tracker-utils/combiner.js";

class MeshObjectTrackerCreator extends Tracker {
    constructor(objDataTracker, objGeoTracker, objMatTracker, evtCtx, shlEvtCtx, emptyObjectLoader, meshObjectLoader, userdata) {
        super();

        this._mapper = new Combiner((key) => new ObjectMapper(evtCtx, shlEvtCtx, emptyObjectLoader, meshObjectLoader), userdata);

        this._typeTrackers = {
            empty: new DictTracker("empty"),
            mesh: new DictTracker("mesh"),
        };

        this._trackerGroup = new TrackerGroup();

        const needsUpdate = () => this.protected_needsUpdate();
        this._mapper.listenNeedsUpdate(needsUpdate);
        this._trackerGroup.listenNeedsUpdate(needsUpdate);

        this._mapper.setSource("dataSet", objDataTracker);
        this._mapper.setSource("geometry", objGeoTracker);
        this._mapper.setSource("material", objMatTracker);

        this._trackerGroup.add(this._typeTrackers.empty);
        this._trackerGroup.add(this._typeTrackers.mesh);
    }

    protected_startUpdate() {
        this._mapper.updateIfNeeded(({ props }) => {
            for (const { key, oldValue, newValue } of props.iterateModified()) {
                if (oldValue) {
                    this._typeTrackers[oldValue.type].deleteValue(key);
                }

                if (newValue) {
                    this._typeTrackers[newValue.type].setValue(key, newValue.object);
                }
            }
        });

        return this._trackerGroup.startUpdate();
    }

    protected_finishUpdate() {
        this._trackerGroup.finishUpdate();
    }
}

class ObjectMapper extends ValueCombiner {
    constructor(evtCtx, shlEvtCtx, emptyObjectLoader, meshObjectLoader) {
        super();

        this._evtCtx = evtCtx;
        this._shlEvtCtx = shlEvtCtx;
        this._emptyObjectLoader = emptyObjectLoader;
        this._meshObjectLoader = meshObjectLoader;

        this._dataSetTracker = new ValueTracker();
        this._geoTracker = new ValueTracker();
        this._matTracker = new ValueTracker();
        this._multiMatTracker = new PropTracker();

        this._object = null;
        this._outputTracker = new ValueTracker();

        const needsUpdate = () => this.protected_needsUpdate();
        this._dataSetTracker.listenNeedsUpdate(needsUpdate);
        this._geoTracker.listenNeedsUpdate(needsUpdate);
        this._matTracker.listenNeedsUpdate(needsUpdate);
        this._multiMatTracker.listenNeedsUpdate(needsUpdate);
    }

    setValue(key, value) {
        switch (key) {
            case "dataSet":
                this._dataSetTracker.setValue(value);
                break;

            case "geometry":
                this._geoTracker.setValue(value);
                break;

            case "material":
                this._matTracker.setValue(value);
                break;
        }
    }

    deleteValue(key) {
        switch (key) {
            case "dataSet":
                this._dataSetTracker.deleteValue();
                break;

            case "geometry":
                this._geoTracker.deleteValue();
                break;

            case "material":
                this._matTracker.deleteValue();
                break;
        }
    }

    protected_startUpdate() {
        const materialModified = this._matTracker.needsUpdate;

        this._dataSetTracker.update(({ prop: { oldValue: oldDataSet, newValue: newDataSet } }) => {
            this._geoTracker.update(({ prop: { oldValue: oldGeometry, newValue: newGeometry } }) => {
                this._matTracker.update(({ prop: { oldValue: oldMaterial, newValue: newMaterial } }) => {
                    const materialDeepModified = materialModified || this._multiMatTracker.needsUpdate;

                    if (materialModified) {
                        if (Array.isArray(newMaterial)) {
                            this._multiMatTracker.track(newMaterial, this._shlEvtCtx.getEvents(newMaterial));
                        } else {
                            this._multiMatTracker.track();
                        }
                    }

                    this._multiMatTracker.update();

                    const oldType = oldDataSet ? (oldGeometry && oldMaterial ? "mesh" : "empty") : "none";
                    const newType = newDataSet ? (newGeometry && newMaterial ? "mesh" : "empty") : "none";

                    if (oldType !== newType) {
                        switch (oldType) {
                            case "empty":
                                this._emptyObjectLoader.dispose(this._object);
                                break;

                            case "mesh":
                                this._meshObjectLoader.dispose(this._object);
                                break;
                        }

                        switch (newType) {
                            case "none":
                                this._object = null;
                                this._outputTracker.deleteValue();
                                break;

                            case "empty":
                                this._object = this._emptyObjectLoader.create();
                                this._outputTracker.setValue({ type: "empty", object: this._object });
                                break;

                            case "mesh":
                                this._object = this._meshObjectLoader.create(newGeometry, newMaterial);
                                this._outputTracker.setValue({ type: "mesh", object: this._object });
                                break;
                        }
                    } else if (newType === "mesh") {
                        if (newGeometry !== oldGeometry) {
                            this._meshObjectLoader.setGeometry(this._object, newGeometry);
                        }

                        if (materialDeepModified) {
                            this._meshObjectLoader.setMaterial(this._object, newMaterial);
                        }
                    }
                });
            });
        });

        return this._outputTracker.startUpdate();
    }

    protected_finishUpdate() {
        this._outputTracker.finishUpdate();
    }
}

export default MeshObjectTrackerCreator;
